import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Show } from "@clerk/react";
import { Toaster } from "@/components/ui/sonner";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import { MeProvider, useMe } from "@/components/portal/MeContext";
import Home from "@/pages/Home";
import { SignInPage, SignUpPage } from "@/pages/AuthPages";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import PortalLayout from "@/pages/portal/PortalLayout";
import Dashboard from "@/pages/portal/Dashboard";
import Projects from "@/pages/portal/Projects";
import ProjectDetail from "@/pages/portal/ProjectDetail";
import DocsPage from "@/pages/portal/DocsPage";
import PrintDoc from "@/pages/portal/PrintDoc";
import TimeClock from "@/pages/portal/TimeClock";
import Tasks from "@/pages/portal/Tasks";
import Users from "@/pages/portal/Users";
import Leads from "@/pages/portal/Leads";
import Communications from "@/pages/portal/Communications";
import GalleryAdmin from "@/pages/portal/GalleryAdmin";
import TransformationsAdmin from "@/pages/portal/TransformationsAdmin";
import CalendarPage from "@/pages/portal/CalendarPage";
import Appointments from "@/pages/portal/Appointments";
import Newsletter from "@/pages/portal/Newsletter";
import SocialSettings from "@/pages/portal/SocialSettings";
import Activity from "@/pages/portal/Activity";
import WorkItem from "@/pages/WorkItem";

const Protected = ({ children }) => (
  <>
    <Show when="signed-in"><MeProvider>{children}</MeProvider></Show>
    <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
  </>
);

const RoleGate = ({ roles, children }) => {
  const me = useMe();
  return roles.includes(me.role) ? children : <Navigate to="/portal" replace />;
};

function App() {
  return (
    <BrowserRouter>
      <ClerkAuthProvider>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/sign-in/*" element={<SignInPage />} />
          <Route path="/sign-up/*" element={<SignUpPage />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/work/:id" element={<WorkItem />} />
          <Route path="/portal/print/:kind/:id" element={<Protected><PrintDoc /></Protected>} />
          <Route path="/portal" element={<Protected><PortalLayout /></Protected>}>
            <Route index element={<Dashboard />} />
            <Route path="projects" element={<Projects />} />
            <Route path="projects/:id" element={<ProjectDetail />} />
            <Route path="estimates" element={<RoleGate roles={["admin", "customer"]}><DocsPage kind="estimates" /></RoleGate>} />
            <Route path="invoices" element={<RoleGate roles={["admin", "customer"]}><DocsPage kind="invoices" /></RoleGate>} />
            <Route path="time" element={<RoleGate roles={["admin", "employee"]}><TimeClock /></RoleGate>} />
            <Route path="tasks" element={<RoleGate roles={["admin", "employee"]}><Tasks /></RoleGate>} />
            <Route path="leads" element={<RoleGate roles={["admin"]}><Leads /></RoleGate>} />
            <Route path="contacts" element={<RoleGate roles={["admin"]}><Communications /></RoleGate>} />
            <Route path="users" element={<RoleGate roles={["admin"]}><Users /></RoleGate>} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="appointments" element={<RoleGate roles={["admin"]}><Appointments /></RoleGate>} />
            <Route path="gallery" element={<RoleGate roles={["admin"]}><GalleryAdmin /></RoleGate>} />
            <Route path="before-after" element={<RoleGate roles={["admin"]}><TransformationsAdmin /></RoleGate>} />
            <Route path="newsletter" element={<RoleGate roles={["admin"]}><Newsletter /></RoleGate>} />
            <Route path="social" element={<RoleGate roles={["admin"]}><SocialSettings /></RoleGate>} />
            <Route path="activity" element={<RoleGate roles={["admin"]}><Activity /></RoleGate>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="bottom-right" richColors />
      </ClerkAuthProvider>
    </BrowserRouter>
  );
}

export default App;
